/**
 * The sources behind one statement.
 *
 * TWO THINGS THIS MODAL REFUSES TO DO:
 *
 *   It does not show a link or an excerpt for evidence whose licence forbids
 *   display. Such evidence can corroborate inside the validator; republishing it
 *   here would be the product doing what the licence says it may not.
 *
 *   It does not present two providers returning one page as two sources. Sources
 *   are grouped by independence group — the identity of the SOURCE, not of the
 *   provider that surfaced it — and the count shown is of groups.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { ExternalLink } from 'lucide-react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { openExternalUrl } from '@/lib/linking';
import type {
  ClaimSourceRole,
  ClaimView,
  EvidenceView,
} from '@/lib/strategic-analysis/types';

const QUALITY_LABELS: Record<EvidenceView['sourceQuality'], string> = {
  primary_official: 'Official source',
  regulatory: 'Regulator',
  statistical: 'Official statistics',
  company_official: 'Company source',
  reputable_secondary: 'Secondary source',
  unclassified: 'Unclassified source',
};

const SUPPORT_LABELS: Record<ClaimView['supportState'], string> = {
  supported: 'Supported by the sources below',
  partially_supported: 'Partly supported',
  conflicted: 'Sources disagree',
  unsupported: 'Not supported',
  not_verifiable: 'Could not be verified',
};

const KIND_LABELS: Record<ClaimView['kind'], string> = {
  fact: 'Fact',
  calculation: 'Calculated in code',
  estimate: 'Estimate',
  inference: 'AI analysis',
  point_to_consider: 'Point to consider',
  unknown: 'Unknown',
};

/** What the bounded counter-evidence search did — never what it proves. */
const COUNTER_EVIDENCE_LABELS: Record<ClaimView['counterEvidenceStatus'], string> = {
  completed_with_results: 'Evidence against this was searched for and found. It is shown with the sources.',
  none_found_after_search:
    'Evidence against this was searched for and none was found within that search. That is a limit of the search, not proof that none exists.',
  provider_failed: 'The search for evidence against this could not be completed — a source was unavailable.',
  filter_not_supported:
    'The search for evidence against this ran more broadly than intended, because a filter could not be applied.',
  budget_exhausted: 'The research budget was reached before evidence against this could be searched for.',
  not_required: 'No counter-evidence search was required for this statement.',
};

interface SourceEvidenceModalProps {
  visible: boolean;
  onClose: () => void;
  claim: ClaimView | null;
  sources: EvidenceView[];
}

export function SourceEvidenceModal({ visible, onClose, claim, sources }: SourceEvidenceModalProps) {
  const colors = useThemeColors();

  // GROUPED BY WHAT EACH SOURCE DOES, not by the fact that it is attached.
  //
  // Every link used to be rendered under "N independent sources" above the
  // claim's statement, which reads as corroboration — including for material a
  // counter-evidence search returned and nothing has classified. The role
  // column has existed since the feature shipped; the client discarded it.
  const roleByEvidenceId = new Map((claim?.links ?? []).map((l) => [l.evidenceId, l.role]));
  const cited = claim ? sources.filter((s) => roleByEvidenceId.has(s.evidenceId)) : [];
  const byRole = (role: ClaimSourceRole) =>
    cited.filter((s) => roleByEvidenceId.get(s.evidenceId) === role);
  const supporting = byRole('supports');
  const weakening = byRole('weakens');
  const conflicting = byRole('conflicts');
  const contextual = byRole('context');

  // INDEPENDENT SUPPORTING GROUPS, counted apart from everything else. A count
  // that mixes in contrary and unclassified material overstates corroboration
  // by exactly the amount that matters.
  const supportingGroups = new Set(supporting.map((s) => s.independenceGroup)).size;

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title="Sources"
      footer={
        <AppModal.Actions>
          <ModalAction label="Close" onPress={onClose} variant="neutral" />
        </AppModal.Actions>
      }
    >
      {!claim
        ? (
          <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
            Select a statement to see its sources.
          </Text>
        )
        : (
          <View className="gap-4">
            <Text className="text-base leading-6" style={{ color: colors.text.primary }}>
              {claim.statement}
            </Text>

            <View className="flex-row flex-wrap gap-1.5">
              <Chip label={KIND_LABELS[claim.kind]} colors={colors} />
              <Chip label={SUPPORT_LABELS[claim.supportState]} colors={colors} />
            </View>

            <View className="rounded-xl p-3.5" style={{ backgroundColor: colors.surface.secondary }}>
              <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
                {COUNTER_EVIDENCE_LABELS[claim.counterEvidenceStatus]}
              </Text>
            </View>

            {cited.length === 0
              ? (
                <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
                  No displayable source is retained for this statement.
                </Text>
              )
              : (
                <>
                  <Text className="text-xs font-semibold uppercase" style={{ color: colors.text.tertiary }}>
                    {supportingGroups === 0
                      // The honest headline when nothing retained has been
                      // classified as supporting. Saying "supported by the
                      // sources below" over context-only material is the claim
                      // this modal exists to avoid making.
                      ? 'Retained sources — none classified as supporting'
                      : supportingGroups === 1
                      ? '1 independent supporting source'
                      : `${supportingGroups} independent supporting sources`}
                  </Text>
                  {(weakening.length > 0 || conflicting.length > 0) && (
                    <Text className="text-xs font-semibold uppercase" style={{ color: colors.amber.main }}>
                      {`${weakening.length + conflicting.length} source(s) argue against this`}
                    </Text>
                  )}
                  {contextual.length > 0 && supportingGroups === 0 && (
                    <Text className="text-[12px] leading-4" style={{ color: colors.text.secondary }}>
                      These sources were retained while researching this statement. Nothing has
                      established what they say about it.
                    </Text>
                  )}
                  {cited.map((source) => (
                    <View
                      key={source.evidenceId}
                      className="rounded-xl p-3 gap-2"
                      style={{
                        backgroundColor: colors.surface.primary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                        overflow: 'hidden',
                      }}
                    >
                      <RoleChip role={roleByEvidenceId.get(source.evidenceId) ?? 'context'} colors={colors} />
                      <Text numberOfLines={2} className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                        {source.title ?? source.publisher ?? 'Retained source'}
                      </Text>
                      <View className="flex-row flex-wrap gap-1.5">
                        <Chip label={QUALITY_LABELS[source.sourceQuality]} colors={colors} />
                        {source.publisher && <Chip label={source.publisher} colors={colors} />}
                        {source.publishedAt && (
                          <Chip label={`Published ${source.publishedAt.slice(0, 10)}`} colors={colors} />
                        )}
                        {source.retrievedAt && (
                          <Chip label={`Retrieved ${source.retrievedAt.slice(0, 10)}`} colors={colors} />
                        )}
                      </View>

                      {source.canDisplay
                        ? (
                          <>
                            {source.excerpt && (
                              <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
                                “{source.excerpt}”
                              </Text>
                            )}
                            {source.canonicalUrl && (
                              <View className="flex-row items-center gap-1.5">
                                <ExternalLink size={14} color={colors.primary[700]} />
                                <Text
                                  accessibilityRole="link"
                                  numberOfLines={1}
                                  onPress={() => openExternalUrl(source.canonicalUrl!)}
                                  className="flex-1 text-sm font-semibold"
                                  style={{ color: colors.primary[700] }}
                                >
                                  Open source
                                </Text>
                              </View>
                            )}
                          </>
                        )
                        : (
                          <Text className="text-sm leading-5" style={{ color: colors.text.tertiary }}>
                            This source’s licence does not permit showing its text or link here.
                          </Text>
                        )}
                    </View>
                  ))}
                </>
              )}
          </View>
        )}
    </AppModal>
  );
}

function Chip({ label, colors }: { label: string; colors: ReturnType<typeof useThemeColors> }) {
  return (
    <View className="rounded-full px-2 py-1" style={{ backgroundColor: colors.surface.secondary }}>
      <Text numberOfLines={1} className="text-xs" style={{ color: colors.text.secondary }}>
        {label}
      </Text>
    </View>
  );
}

/**
 * What this source does for the claim, in one word.
 *
 * `context` is not a hedge — it is the accurate description of a source that was
 * retained while researching the statement and has not been classified. The
 * alternative, leaving it unlabelled among the others, is what let contrary and
 * unexamined material read as corroboration.
 */
const ROLE_LABELS: Record<ClaimSourceRole, string> = {
  supports: 'Supports',
  weakens: 'Argues against',
  conflicts: 'Contradicts',
  context: 'Context — relation not established',
};

function RoleChip({
  role,
  colors,
}: {
  role: ClaimSourceRole;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const tone = role === 'supports'
    ? colors.primary[700]
    : role === 'context'
    ? colors.text.tertiary
    : colors.amber.main;
  return (
    <Text
      numberOfLines={1}
      className="text-[11px] font-semibold uppercase"
      style={{ color: tone, letterSpacing: 0.4 }}
    >
      {ROLE_LABELS[role]}
    </Text>
  );
}
